import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Pin } from "lucide-react";
import api from "@/lib/api";
import { BoardTag, RoomNumber } from "@/components/mall/BillboardKit";

const INK = "#111111";
const PAPER = "#f2eee4";
const SERIF = "Georgia, 'Times New Roman', Times, serif";

const RULES = [
    "You can post machines, supplies, blanks, apparel, classes, services and wanted ads.",
    "Any shop can pin one ad to the top of this page. The pin falls off after 24 hours.",
    "You can pin it again any time you want it back on top.",
    "13thav takes no commission on anything sold here. Posting is part of every plan.",
];

/** Section name and how many ads fit under it on one page. The rest are one tap away. */
const SECTIONS = {
    machine: ["Machines", 3], supply: ["Supplies", 5], blank: ["Blanks", 2],
    apparel: ["Apparel", 2], class: ["Classes", 1], wanted: ["Wanted", 2],
};

const short = (t, n = 82) => {
    const s = (t || "").replace(/\s+/g, " ").trim();
    if (s.length <= n) return s;
    return `${s.slice(0, n).replace(/\s+\S*$/, "")}…`;
};
const money = (p) => (p ? `$${Number(p).toLocaleString()}` : "Make an offer");

/** A fresh deal every time the page loads: pinned first, featured next, everything else shuffled. */
const shuffle = (rows) => {
    const out = [...rows];
    for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
};

/** A black bar with white type, the way the old pages split the sections. */
function Header({ children, kind }) {
    return (
        <p className="my-2 px-2 py-1 text-center text-[13px] font-black uppercase tracking-[0.18em]"
            style={{ background: INK, color: "#ffffff", fontFamily: SERIF }} data-testid={`classifieds-section-${kind}`}>{children}</p>
    );
}

/** One real ad. Every one of them opens the listing it came from. */
function Ad({ a, lead = false }) {
    const boxed = a.pinned || a.featured || lead;
    return (
        <Link to={`/supplies/${a.slug}`} data-testid={`classifieds-ad-${a.slug}`}
            className={`block transition-colors hover:bg-black/[0.04] ${boxed ? `my-2 border-2 p-2.5 ${a.pinned ? "border-dotted" : ""}` : "py-2.5"}`}
            style={{ borderColor: INK, fontFamily: SERIF, color: INK }}>
            {(a.pinned || a.featured) && (
                <p className="-mt-2.5 mb-1.5 inline-block px-2 py-0.5 text-[11px] font-black uppercase tracking-[0.18em]"
                    style={{ background: INK, color: "#ffffff" }} data-testid={a.pinned ? `classifieds-pinned-${a.slug}` : undefined}>
                    {a.pinned ? "Pinned" : "Featured"}
                </p>
            )}
            <p className="text-[12px] font-black uppercase leading-tight">{a.title}</p>
            {a.description && <p className="mt-1 text-[12px] leading-[15px]">{short(a.description)}</p>}
            <p className="mt-1 text-[11px] leading-[14px]">
                <span className="font-bold">{money(a.price)}</span>
                {a.location ? ` · ${a.location}` : ""}{a.store?.name ? ` · ${a.store.name}` : ""}
            </p>
        </Link>
    );
}

/** A section of the page: the black header, the ads under it, and a line to the rest when it runs long. */
function Section({ kind, ads }) {
    if (!ads.length) return null;
    const [label, cap] = SECTIONS[kind];
    return (
        <>
            <Header kind={kind}>{label}</Header>
            {ads.slice(0, cap).map((a, i) => <Ad key={a.slug} a={a} lead={i === 0} />)}
            {ads.length > cap && (
                <Link to={`/supplies?kind=${kind}`} className="block py-2 text-[11px] uppercase tracking-[0.16em] hover:underline"
                    style={{ color: INK, fontFamily: SERIF }} data-testid={`classifieds-more-${kind}`}>
                    {ads.length - cap} more under {label} <ArrowRight size={10} className="ml-1 inline" />
                </Link>
            )}
        </>
    );
}

/** Room 06 — the classifieds page, laid out like the back page of an old newspaper. Every ad is real. */
export default function Classifieds() {
    const [ads, setAds] = useState([]);
    useEffect(() => {
        api.get("/marketplace/listings").then(({ data }) => setAds(data.listings || [])).catch(() => { });
    }, []);

    const by = useMemo(() => {
        const g = Object.fromEntries(Object.keys(SECTIONS).map((k) => [k, []]));
        const rank = (a) => (a.pinned ? 0 : a.featured ? 1 : 2);
        shuffle(ads).sort((x, y) => rank(x) - rank(y)).forEach((a) => { if (g[a.kind]) g[a.kind].push(a); });
        return g;
    }, [ads]);

    const today = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    const rule = { borderColor: INK };

    return (
        <section className="relative overflow-hidden border-y" data-testid="mall-room-classifieds"
            style={{ background: PAPER, borderColor: "#d6d3d1" }}>
            <div className="pointer-events-none absolute inset-0 opacity-[0.28]"
                style={{ backgroundImage: "radial-gradient(#a8a29e 0.6px, transparent 0.6px)", backgroundSize: "3px 3px" }} />

            <div className="relative mx-auto max-w-7xl px-4 py-8 sm:px-6 md:py-10">
                <div className="flex flex-wrap items-end justify-between gap-3">
                    <BoardTag room="classifieds" kind="plaza" note="the classifieds page" />
                    <RoomNumber n="06" room="Classifieds" accent={INK} />
                </div>

                {/* masthead */}
                <div className="mt-4 border-b-[6px]" style={rule} data-testid="classifieds-masthead">
                    <h2 className="text-center text-[15vw] font-black uppercase leading-[0.85] tracking-[-0.04em] sm:text-[9rem] lg:text-[11rem]"
                        style={{ fontFamily: "'Times New Roman', Times, serif", color: INK, transform: "scaleY(1.15)", transformOrigin: "bottom" }}
                        data-testid="classifieds-headline">
                        Classifieds
                    </h2>
                </div>
                <div className="mt-1 flex flex-wrap items-center justify-between gap-2 border-b py-1 text-[11px] uppercase tracking-[0.2em]"
                    style={{ ...rule, color: INK, fontFamily: SERIF }}>
                    <span>Vol. 13 · No. 1</span>
                    <span className="hidden sm:inline">{today}</span>
                    <span data-testid="classifieds-count">{ads.length} live ads · A fresh page every visit · Free to post</span>
                </div>

                {/* four ruled columns — one page, never taller */}
                <div className="mt-3 grid gap-x-5 sm:grid-cols-2 lg:max-h-[560px] lg:grid-cols-4 lg:overflow-hidden" data-testid="classifieds-columns">
                    <div className="divide-y lg:border-r lg:pr-5" style={rule}>
                        <div className="my-2 border-2 border-dotted p-3" style={{ borderColor: INK, fontFamily: SERIF, color: INK }}>
                            <p className="text-[13px] font-black uppercase leading-tight">How this page works</p>
                            <p className="mt-1.5 text-[12px] leading-[15px]" data-testid="classifieds-body">
                                This is the classifieds page for every print shop on 13thav. Shops post the machines, supplies,
                                blanks and classes they want to sell, and the things they want to buy. You read the ad, you message
                                the seller, and you make the deal yourselves. 13thav does not take a cut.
                            </p>
                            <ul className="mt-2 space-y-1" data-testid="classifieds-rules">
                                {RULES.map((t) => <li key={t} className="text-[11px] leading-[14px]">• {t}</li>)}
                            </ul>
                        </div>
                        <Section kind="machine" ads={by.machine} />
                    </div>

                    <div className="divide-y lg:border-r lg:pr-5" style={rule}>
                        <Section kind="supply" ads={by.supply} />
                    </div>

                    <div className="divide-y lg:border-r lg:pr-5" style={rule}>
                        <Section kind="blank" ads={by.blank} />
                        <Section kind="apparel" ads={by.apparel} />
                    </div>

                    <div className="divide-y" style={rule}>
                        <Section kind="class" ads={by.class} />
                        <Section kind="wanted" ads={by.wanted} />
                        <div className="my-2 p-3 text-center" style={{ background: INK, color: "#ffffff", fontFamily: SERIF }} data-testid="classifieds-post-box">
                            <p className="text-[15px] font-black uppercase leading-tight">Your ad here</p>
                            <p className="mt-1.5 text-[12px] leading-[15px]" style={{ color: "#e7e5e4" }}>
                                Every shop on 13thav can post here. It is free on every plan, and buyers message you on the site.
                            </p>
                            <div className="mt-3 flex flex-col gap-2">
                                <Link to="/supplies/sell" data-testid="room-classifieds-post"
                                    className="block border-2 px-3 py-2 text-[12px] font-black uppercase tracking-[0.14em] transition-colors hover:bg-white/10"
                                    style={{ borderColor: "#ffffff", color: "#ffffff" }}>
                                    Post an ad
                                </Link>
                                <Link to="/supplies" data-testid="room-classifieds-cta"
                                    className="block px-3 py-2 text-[12px] font-black uppercase tracking-[0.14em] transition-transform hover:scale-[1.02]"
                                    style={{ background: "#ffffff", color: INK }}>
                                    Read the classifieds
                                </Link>
                            </div>
                        </div>
                        <Link to="/account/listings" data-testid="room-classifieds-pin-link"
                            className="block py-2.5 text-[11px] uppercase tracking-[0.16em] transition-colors hover:underline"
                            style={{ color: INK, fontFamily: SERIF }}>
                            <Pin size={10} className="mr-1 inline" /> Shop owners: pin your ad to the top for 24 hours <ArrowRight size={10} className="ml-1 inline" />
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}
